import {
  FieldWrapperProps,
  FormField,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { useEffect } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useUnmount } from 'react-use';

import { OAuthStrategy } from 'apis/partnerIntegrations';
import { HookFormSelectionList } from 'components/hookForm';
import { useBreakpointValue } from 'utils/mediaQuery';

import { OAuthForm } from './components/form/oAuthForm';
import { OAuthFormData } from './oAuth.types';
import { useLazyReadOAuthProvider } from './utils/useLazyReadOAuthProvider/useLazyReadOAuthProvider';
import { useLazyReadOAuthStrategySchema } from './utils/useLazyReadOAuthStrategySchema/useLazyReadOAuthStrategySchema';
import { useReadOAuthStrategies } from './utils/useReadOAuthStrategies/useReadOAuthStrategies';

type OAuthProps = {
  disabled: boolean;
  parentOAuthId?: string;
  onChangeParentOAuthId: (value?: string) => void;
  onChangeOAuthStrategy: (value?: string) => void;
  handleParentSubmit: () => void;
  meta: FieldWrapperProps['meta'];
};

export const OAuth = ({
  disabled,
  parentOAuthId,
  onChangeParentOAuthId,
  onChangeOAuthStrategy,
  handleParentSubmit,
  meta,
}: OAuthProps): JSX.Element => {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const { reset: resetOAuthForm } = useFormContext<OAuthFormData>();
  const { strategies, isStrategiesError } = useReadOAuthStrategies();

  const strategiesForm = useForm<{ selectedStrategy: OAuthStrategy[] }>({
    defaultValues: { selectedStrategy: [] },
  });

  const [selectedStrategy] = useWatch({
    control: strategiesForm.control,
    name: 'selectedStrategy',
  });

  const {
    readOAuthStrategySchema,
    oAuthSchema,
    setOAuthSchemaSchema,
    isSchemaLoading,
    isOAuthSchemaError,
  } = useLazyReadOAuthStrategySchema();

  const {
    readOAuthProvider,
    oAuthProvider,
    setOAuthProvider,
    isOAuthProviderLoading,
    isOAuthProviderError,
  } = useLazyReadOAuthProvider();

  useUnmount(() => {
    resetOAuthState();
    onChangeParentOAuthId(undefined);
  });

  const resetOAuthState = () => {
    setOAuthProvider(undefined);
    setOAuthSchemaSchema([]);
    strategiesForm.reset({ selectedStrategy: [] });
    onChangeOAuthStrategy(undefined);
    resetOAuthForm({});
  };

  const gqlErrorMessage = (): string | undefined => {
    if (isStrategiesError) {
      return 'Something went wrong fetching the strategies';
    }

    if (isOAuthSchemaError) {
      return 'Something went wrong fetching the schema';
    }

    if (isOAuthProviderError) {
      return 'Something went wrong fetching the provider';
    }

    return undefined;
  };

  // Getting the OAuth provider from the OAuth id
  useEffect(() => {
    if (parentOAuthId && !oAuthProvider && !isOAuthProviderLoading) {
      readOAuthProvider({
        variables: {
          id: parentOAuthId,
        },
      });
    }
  }, [parentOAuthId, oAuthProvider, isOAuthProviderLoading, readOAuthProvider]);

  // Setting the strategiesForm state to the strategy from the oAuthProvider
  useEffect(() => {
    if (!isOAuthProviderLoading && oAuthProvider && strategies.length > 0) {
      strategiesForm.reset({
        selectedStrategy:
          [
            strategies?.find(
              (strategy) => strategy.name === oAuthProvider.strategy,
            ),
          ] || [],
      });

      onChangeOAuthStrategy(oAuthProvider.strategy);
    }
  }, [oAuthProvider, isOAuthProviderLoading, strategies]);

  // Get the schema once the user selected a strategy
  useEffect(() => {
    if (selectedStrategy?.name) {
      readOAuthStrategySchema({
        variables: {
          id: selectedStrategy.name,
        },
      });

      onChangeOAuthStrategy(selectedStrategy.name);
    }
  }, [selectedStrategy, readOAuthStrategySchema]);

  return (
    <>
      <FormField span={isSmallScreen ? 6 : 3}>
        <HookFormSelectionList
          label="OAuth strategies"
          name="selectedStrategy"
          control={strategiesForm.control}
          disabled={disabled}
          componentProps={{
            clearable: true,
            placeholder: 'Select an OAuth strategy',
            getItemTitleByValue: (
              strategy: OAuthStrategy | null | undefined,
            ) => <Text.Paragraph>{strategy?.label}</Text.Paragraph>,
            equalityFn: (item1, item2) => item1.name === item2.name,
          }}
          meta={{
            warning: meta?.error || gqlErrorMessage(),
          }}
        >
          {strategies.map((strategy) => (
            <SelectionList.Item key={strategy.name} value={strategy}>
              <Text.Paragraph>{strategy.label}</Text.Paragraph>
            </SelectionList.Item>
          ))}
        </HookFormSelectionList>
      </FormField>

      {selectedStrategy?.name &&
        (isSchemaLoading ? (
          <Spinner />
        ) : (
          <FormField>
            <OAuthForm
              disabled={disabled}
              selectedStrategy={selectedStrategy}
              oAuthSchema={oAuthSchema}
              oAuthProvider={oAuthProvider}
              handleParentSubmit={handleParentSubmit}
              onChangeParentOAuthId={onChangeParentOAuthId}
              setOAuthProvider={setOAuthProvider}
            />
          </FormField>
        ))}
    </>
  );
};
